import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChargerNote,
  ExternalLink,
  LocationNote,
  MaintenaceNoteUpdate,
  ModelNote,
  SparkIcon,
  TicketJira,
  TicketZendesk,
} from '../../assets/icons';
import {
  SupportNote,
  SupportNoteSource,
  SupportNoteTag,
} from '../../stores/types/supportnote.interface';
import { formatDate } from '../../utils/Date.Util';
import { ColorType, Icon, Label, LabelType, Pill } from '../_ui';
import { IconSize } from '../../constant/IconSize.constant';
import { Tooltip, TooltipContent, TooltipTrigger } from '../_ui/Tooltip.component';
import { ChargerHistoryTimelineItemProps } from './ChargerHistory.interface';
import { formatLink } from './ChargerHistoryUtils';

const getIconAndTooltip = (
  item: SupportNote,
  t: (key: string) => string,
): { icon: string; tooltip: string } => {
  if (item.source === SupportNoteSource.MAINTENANCE) {
    return {
      icon: MaintenaceNoteUpdate,
      tooltip: t('charger_history_tooltip_maintenance_update'),
    };
  }

  if (item.source === SupportNoteSource.JIRA) {
    return {
      icon: TicketJira,
      tooltip: t('charger_history_tooltip_jira_ticket'),
    };
  }

  if (item.source === SupportNoteSource.ZENDESK) {
    return {
      icon: TicketZendesk,
      tooltip: t('charger_history_tooltip_zendesk_ticket'),
    };
  }

  if (item.source === SupportNoteSource.LABEL) {
    return {
      icon: SparkIcon,
      tooltip: t('charger_history_tooltip_label'),
    };
  }

  switch (item.tag) {
    case SupportNoteTag.CHARGER:
      return {
        icon: ChargerNote,
        tooltip: t('charger_history_tooltip_charger_note'),
      };
    case SupportNoteTag.LOCATION:
      return {
        icon: LocationNote,
        tooltip: t('charger_history_tooltip_location_note'),
      };
    case SupportNoteTag.MODEL:
      return {
        icon: ModelNote,
        tooltip: t('charger_history_tooltip_model_note'),
      };
    default:
      return {
        icon: ChargerNote,
        tooltip: t('charger_history_tooltip_charger_note'),
      };
  }
};

// Zendesk's status vocabulary is fixed and fully covered here (open/new/pending/hold/solved/
// closed, per the design's status legend -- colors below are sampled from the actual Figma frame,
// not guessed). Jira's is project-configurable -- these are the values actually seen in
// tracker_integration_jiraticketanalysis (no design spec covers Jira colors, so they're mapped by
// analogy to the nearest Zendesk state); anything else falls back to grey.
const STATUS_PILL_COLOR: Record<string, ColorType> = {
  // Zendesk
  open: ColorType.NEGATIVE,
  new: ColorType.WARNING2,
  pending: ColorType.DENIM,
  hold: ColorType.BLACK,
  'on-hold': ColorType.BLACK,
  solved: ColorType.GREY6,
  closed: ColorType.GREY6,
  // Jira
  backlog: ColorType.WARNING2, // ~ new
  'escalated to oem': ColorType.DENIM, // ~ pending
  blocked: ColorType.BLACK, // ~ hold
  resolved: ColorType.GREY6, // ~ solved
  done: ColorType.GREY6, // ~ closed
  'will not do': ColorType.GREY6, // ~ closed
};

// The orange "New"/"Backlog" pill uses black text in the design (white reads poorly on that
// lightness of orange); every other pill uses white.
const DARK_TEXT_STATUSES = new Set(['new', 'backlog']);

const getStatusPillColor = (status: string): ColorType =>
  STATUS_PILL_COLOR[status.trim().toLowerCase()] ?? ColorType.GREY6;

const getStatusPillTextColor = (status: string): ColorType =>
  DARK_TEXT_STATUSES.has(status.trim().toLowerCase()) ? ColorType.BLACK : ColorType.WHITE;

const ChargerHistoryTimelineItem: FC<ChargerHistoryTimelineItemProps> = ({
  item,
  isFirst = false,
}) => {
  const { t } = useTranslation();

  const { icon, tooltip } = useMemo(() => getIconAndTooltip(item, t), [item, t]);

  const isJira = item.source === SupportNoteSource.JIRA;
  const isZendesk = item.source === SupportNoteSource.ZENDESK;
  const isTicket = isJira || isZendesk;

  // Ticket rows attribute `createdBy` to the integration itself ("Jira"/"Zendesk"), not a person,
  // so it isn't worth showing in the date line the way it is for notes and maintenance events.
  const formattedDate = item.createdDateTime
    ? formatDate(new Date(item.createdDateTime), 'MMM d, yyyy, h:mm a') +
      (isTicket ? '' : ` by ${item.createdBy}`)
    : '';

  // Zendesk rows in particular often carry "N/A" in `note`; the AI-generated `summary` is the
  // real narrative there, so prefer it whenever `note` isn't actually meaningful.
  const noteBody = isTicket && (!item.note || item.note === 'N/A') ? item.summary : item.note;

  const ticketLink = isTicket ? item.links?.[0] : undefined;
  const viewTicketLabel = isJira
    ? t('charger_history_view_ticket_jira')
    : t('charger_history_view_ticket_zendesk');

  return (
    <div className={`flex flex-row pl-4 gap-3 ${isFirst ? 'mt-4' : ''}`}>
      {/* Timeline column */}
      <div className='flex flex-col items-center flex-shrink-0'>
        <Tooltip placement='top'>
          <TooltipTrigger asChild>
            <div className='flex items-center justify-center cursor-pointer flex-shrink-0'>
              <Icon src={icon} size={IconSize.SIZE_16x16} />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <Label text={tooltip} type={LabelType.BODY3} color={ColorType.WHITE} />
          </TooltipContent>
        </Tooltip>
        <div className='w-0.5 flex-1 flex-shrink-0 bg-black pt-2 my-2' />
      </div>

      {/* Content column */}
      <div className='flex flex-col pb-5 min-w-0 flex-1'>
        {/* Title */}
        <div className='flex flex-row items-center gap-2 flex-wrap'>
          <Label
            text={item.title}
            type={LabelType.LABEL_S}
            color={ColorType.BLACK}
            className='m-0'
          />
          {isTicket && item.ticketId && (
            <Label
              // Zendesk ticket numbers are conventionally shown as "#12345"; Jira issue keys
              // (e.g. "TS-191") already read as an id on their own, so no "#" there.
              text={isZendesk ? `#${item.ticketId}` : item.ticketId}
              type={LabelType.BODY4}
              color={ColorType.GREY5}
              className='m-0'
            />
          )}
          {isTicket && item.ticketStatus && (
            <Pill
              label={item.ticketStatus}
              bgColor={getStatusPillColor(item.ticketStatus)}
              labelColor={getStatusPillTextColor(item.ticketStatus)}
            />
          )}
        </div>

        {/* Date */}
        <Label
          text={formattedDate}
          type={LabelType.BODY4}
          color={ColorType.GREY5}
          className='m-0'
        />

        {/* Note body */}
        {noteBody && (
          <Label
            text={noteBody}
            type={LabelType.BODY4}
            color={ColorType.BLACK}
            className='mt-2 m-0 break-words'
            style={{ fontStyle: 'italic' }}
          />
        )}

        {/* Ticket link: styled "View ticket in Jira/Zendesk" instead of the raw URL */}
        {isTicket && ticketLink && (
          <a
            href={formatLink(ticketLink)}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center gap-1 w-max mt-1 no-underline hover:underline'
          >
            <Label
              text={viewTicketLabel}
              type={LabelType.LABEL_S_MEDIUM}
              color={ColorType.BRAND3}
              className='m-0'
              style={{ fontStyle: 'italic' }}
            />
            <Icon src={ExternalLink} size={IconSize.SIZE_16x16} color={ColorType.BRAND3} />
          </a>
        )}

        {/* Links (non-ticket sources only -- tickets render their single link above) */}
        {!isTicket && item.links && item.links.length > 0 && (
          <div className='flex flex-col gap-0.5 mt-1'>
            {item.links.map((link, index) => (
              <a
                key={`${item.id}-link-${index}`}
                href={formatLink(link)}
                target='_blank'
                rel='noopener noreferrer'
                className='font-inter text-sm font-medium leading-5 no-underline hover:underline break-all text-brand3 italic'
              >
                {link}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChargerHistoryTimelineItem;
